import React from 'react';
import { CostDataPoint, TimeGranularity } from '../../types/costExplorer';
import { getProviderFillClass, PROVIDER_COLORS_WITH_ALL, ProviderColorKey } from '../../utils/providerColors';

/**
 * Cost Chart Component
 * Displays time series cost data as a stacked bar chart, one colored segment
 * per provider (Req 77 AC2 - time granularity visualization). Previously
 * rendered one flat, single-color bar per period showing only the blended
 * total - useful for a trend line, but hid which provider actually drove a
 * cost change. Stacking by provider is the same convention AWS Cost
 * Explorer itself uses for a multi-cloud trend view.
 */
interface CostChartProps {
  data: CostDataPoint[];
  granularity: TimeGranularity;
  currency: string;
}

const PROVIDER_ORDER: ProviderColorKey[] = ['aws', 'azure', 'gcp'];

export const CostChart: React.FC<CostChartProps> = ({ data, granularity, currency }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    switch (granularity) {
      case TimeGranularity.HOURLY:
        return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
      case TimeGranularity.DAILY:
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case TimeGranularity.WEEKLY:
        return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      case TimeGranularity.MONTHLY:
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      case TimeGranularity.QUARTERLY:
        return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
      default:
        return date.toLocaleDateString();
    }
  };

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-border bg-muted/30 dark:border-border dark:bg-card">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  // Which providers actually appear anywhere in this data - drives both the
  // stacking order and the legend, so a single-provider view (e.g. filtered
  // to just Azure) doesn't show a legend entry for providers with no data.
  const providersPresent = PROVIDER_ORDER.filter((p) => data.some((d) => (d.by_provider?.[p] ?? 0) > 0));
  const hasProviderBreakdown = providersPresent.length > 0;

  const maxCost = Math.max(...data.map((d) => d.cost)) * 1.15 || 1;
  const chartHeight = 220;

  return (
    <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-foreground">
          Cost Trend
        </h3>
        {hasProviderBreakdown && (
          <div className="flex flex-wrap gap-3">
            {providersPresent.map((p) => (
              <span key={p} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: PROVIDER_COLORS_WITH_ALL[p] }}
                  aria-hidden="true"
                />
                {p.toUpperCase()}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Chart area */}
      <div role="img" aria-label={hasProviderBreakdown ? 'Cost trend stacked bar chart by provider' : 'Cost trend bar chart'}>
        <div className="flex">
          {/* Y-axis labels */}
          <div className="flex flex-col justify-between pr-2 text-xs text-muted-foreground" style={{ height: `${chartHeight}px` }}>
            <span className="whitespace-nowrap">{formatCurrency(maxCost)}</span>
            <span className="whitespace-nowrap">{formatCurrency(maxCost / 2)}</span>
            <span className="whitespace-nowrap">{formatCurrency(0)}</span>
          </div>

          {/* Bars */}
          <div className="flex-1 relative border-l border-b border-border">
            <div
              className="absolute w-full border-t border-border dark:border-border"
              style={{ bottom: '50%' }}
              aria-hidden="true"
            />
            <div className="flex items-end justify-around h-full px-2 gap-1" style={{ height: `${chartHeight}px` }}>
              {data.map((point, index) => {
                const segments = hasProviderBreakdown
                  ? providersPresent
                      .map((p) => ({ provider: p, cost: point.by_provider?.[p] ?? 0 }))
                      .filter((s) => s.cost > 0)
                  : [{ provider: null as ProviderColorKey | null, cost: point.cost }];

                const tooltipBreakdown = hasProviderBreakdown
                  ? segments.map((s) => `${s.provider!.toUpperCase()}: ${formatCurrency(s.cost)}`).join(', ')
                  : formatCurrency(point.cost);

                return (
                  <div
                    key={index}
                    className="flex w-full max-w-10 flex-col-reverse overflow-hidden rounded-t"
                    style={{ height: `${(point.cost / maxCost) * chartHeight}px` }}
                    title={`${formatDate(point.timestamp)}: ${tooltipBreakdown}`}
                  >
                    {segments.map((s, i) => (
                      <div
                        key={s.provider ?? 'total'}
                        className={`w-full transition-all ${
                          s.provider ? getProviderFillClass(s.provider) : 'bg-primary hover:bg-primary dark:bg-primary dark:hover:bg-primary'
                        } ${i === segments.length - 1 ? 'rounded-t' : ''}`}
                        style={{ height: `${(s.cost / point.cost) * 100}%` }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* X-axis labels */}
        <div className="ml-20 mt-2 flex justify-between text-xs text-muted-foreground">
          {data.length > 0 && (
            <>
              <span>{formatDate(data[0].timestamp)}</span>
              {data.length > 2 && (
                <span>{formatDate(data[Math.floor(data.length / 2)].timestamp)}</span>
              )}
              <span>{formatDate(data[data.length - 1].timestamp)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
